import React, { useEffect, useState } from "react";
import { Input, Modal, Select, Spin } from "antd";
import { svg } from "../../../../../Assets/svg";
import { useAppDispatch } from "../../../../../store";
import {
  getSageProductGroupsAPI,
  updateStorageTypeAPI,
} from "../../storageTypesApis";
import { BtnLoader } from "../../../../../Components/Components";

interface ModifyStorageTypeModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  onSuccess?: () => void;
}

const ModifyStorageTypeModal: React.FC<ModifyStorageTypeModalProps> = ({
  open,
  onClose,
  data,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [selectedSageGroupCode, setSelectedSageGroupCode] = useState<string | null>(null);
  const [sageProductGroups, setSageProductGroups] = useState<any[]>([]);
  const [loadingSageGroups, setLoadingSageGroups] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const storageTypeName =
    data?.name || data?.storage_type || data?.title || data?.code || "";

  useEffect(() => {
    if (open) {
      setSelectedSageGroupCode(
        data?.sage_product_group_code ??
          (data?.sage_product_group_id ? String(data.sage_product_group_id) : null),
      );
      setError("");
      setLoadingSageGroups(true);
      dispatch(
        getSageProductGroupsAPI({
          onSuccess: (res: any) => {
            const groups =
              res?.product_groups ||
              res?.data ||
              (Array.isArray(res) ? res : []);
            setSageProductGroups(groups);
            setLoadingSageGroups(false);
          },
          onError: () => {
            setLoadingSageGroups(false);
            setSageProductGroups([]);
          },
        }),
      );
    }
  }, [open, data, dispatch]);

  const sageGroupOptions = sageProductGroups.map((group: any) => {
    const code = group?.code || (group?.id ? String(group.id) : "");
    const label =
      group?.code || group?.name || group?.description || String(group?.id || group);
    return {
      label,
      value: code,
      original: group,
    };
  });

  const handleSave = () => {
    if (!selectedSageGroupCode) {
      setError("Sage Group is required");
      return;
    }

    const selectedGroup = sageProductGroups.find(
      (g: any) =>
        g.code === selectedSageGroupCode ||
        String(g.id) === String(selectedSageGroupCode),
    );

    const payload = {
      id: data?._id || data?.id,
      name: data?.name,
      sage_product_group_id: selectedGroup?.id
        ? String(selectedGroup.id)
        : data?.sage_product_group_id
        ? String(data.sage_product_group_id)
        : null,
      sage_product_group_code: selectedGroup?.code || selectedSageGroupCode,
    };

    setSaving(true);
    dispatch(
      updateStorageTypeAPI({
        payload,
        onSuccess: () => {
          setSaving(false);
          onSuccess?.();
          onClose();
        },
        onError: () => {
          setSaving(false);
        },
      }),
    );
  };

  return (
    <Modal
      title={
        <div className="text-center fs-4 fw-bolder lh-m">
          Modify Storage Type
        </div>
      }
      open={open}
      onCancel={onClose}
      width={"32.813rem"}
      closeIcon={svg?.closeIcon}
      centered
      className="custom-modal"
      footer={
        <div className="d-flex justify-content-center gap-4">
          <button
            type="button"
            className="btn btn-lg btn-outline-secondary min-w-130 text-uppercase"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-lg btn-primary min-w-130 text-uppercase"
            onClick={handleSave}
            disabled={saving || loadingSageGroups}
          >
            {saving ? <BtnLoader /> : "Save"}
          </button>
        </div>
      }
    >
      <div className="page-form">
        <div className="row gy-3">
          <div className="col-md-12">
            <div className="form-item">
              <label className="custom-label mb-1 ff">Storage Type</label>
              <Input
                className="form-control form-control-lg"
                value={storageTypeName}
                disabled
              />
            </div>
          </div>
          <div className="col-md-12">
            <div className="form-item">
              <label className="custom-label mb-1 ff">
                Sage Group <span className="text-danger">*</span>
              </label>
              <Select
                className="w-100 select-custom"
                size="large"
                placeholder="Select Sage Group"
                loading={loadingSageGroups}
                notFoundContent={
                  loadingSageGroups ? (
                    <div className="text-center p-2">
                      <Spin size="small" />
                    </div>
                  ) : null
                }
                value={selectedSageGroupCode}
                onChange={(val) => {
                  setSelectedSageGroupCode(val);
                  if (val) setError("");
                }}
                showSearch
                optionFilterProp="label"
                options={sageGroupOptions}
                suffixIcon={svg?.selectDownArrow}
                allowClear
              />
              {error && <div className="text-danger fs-8 mt-1">{error}</div>}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModifyStorageTypeModal;
